const SERIES = ['#38bdf8', '#f59e0b', '#a78bfa', '#34d399', '#f472b6', '#fb7185', '#22d3ee'];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function xDomain(characteristics) {
  const xs = characteristics.flatMap(c => (c.history || []).map(p => Number(p.partCount))).filter(Number.isFinite);
  if (!xs.length) return [0, 1];
  const lo = Math.min(...xs);
  const hi = Math.max(...xs);
  return lo === hi ? [lo - 1, hi + 1] : [lo, hi];
}

function normalized(c, value) {
  if (!Number.isFinite(Number(value))) return 0;
  const target = Number(c.target) || 0;
  const v = Number(value);
  const room = v >= target ? Number(c.hi) - target : target - Number(c.lo);
  return room > 0 ? (v - target) / room : 0;
}

function pointsPath(points, x, y) {
  if (!points.length) return '';
  return points.map((p, i) => `${i ? 'L' : 'M'} ${x(p).toFixed(1)} ${y(p).toFixed(1)}`).join(' ');
}

export function MiniSparkline({ c }) {
  const history = c.history || [];
  if (history.length < 2) {
    return <div className="h-9 flex items-center text-[10px] text-slate-600">waiting for history</div>;
  }

  const width = 132;
  const height = 36;
  const pad = 3;
  const values = history.map(p => Number(p.value)).filter(Number.isFinite);
  let lo = Math.min(Number(c.lo), ...values);
  let hi = Math.max(Number(c.hi), ...values);
  const span = Math.max(1, hi - lo);
  lo -= span * 0.08;
  hi += span * 0.08;

  const x = (_, i) => pad + (i / Math.max(1, history.length - 1)) * (width - pad * 2);
  const y = p => height - pad - ((Number(p.value) - lo) / (hi - lo)) * (height - pad * 2);
  const path = history.map((p, i) => `${i ? 'L' : 'M'} ${x(p, i).toFixed(1)} ${y(p).toFixed(1)}`).join(' ');
  const targetY = height - pad - ((Number(c.target) - lo) / (hi - lo)) * (height - pad * 2);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-9" aria-hidden="true">
      <line x1={pad} x2={width - pad} y1={targetY} y2={targetY} stroke="rgba(148,163,184,.18)" strokeDasharray="3 3" />
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
      <circle cx={x(history.at(-1), history.length - 1)} cy={y(history.at(-1))} r="2.5" fill="currentColor" />
    </svg>
  );
}

export function TrendChart({ characteristic: c, actionLimit = 0.6, events = [] }) {
  const history = c?.history || [];
  if (!c || history.length < 2) return <EmptyChart />;

  const W = 920;
  const H = 280;
  const m = { l: 58, r: 24, t: 20, b: 34 };
  const plotW = W - m.l - m.r;
  const plotH = H - m.t - m.b;
  const [x0, x1] = xDomain([c]);
  const values = history.map(p => Number(p.value)).filter(Number.isFinite);
  let y0 = Math.min(Number(c.lo), ...values);
  let y1 = Math.max(Number(c.hi), ...values);
  const span = Math.max(1, y1 - y0);
  y0 -= span * 0.12;
  y1 += span * 0.12;

  const x = p => m.l + ((Number(p.partCount) - x0) / Math.max(1, x1 - x0)) * plotW;
  const yv = v => m.t + (1 - (Number(v) - y0) / Math.max(1e-9, y1 - y0)) * plotH;
  const y = p => yv(p.value);
  const path = pointsPath(history, x, y);

  const target = Number(c.target);
  const loAction = target - actionLimit * (target - Number(c.lo));
  const hiAction = target + actionLimit * (Number(c.hi) - target);

  const charEvents = events.filter(e => e.characteristicId === c.id && Number.isFinite(Number(e.partCountAtProposal)));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-h-[250px]" role="img" aria-label={`${c.id} trend over parts`}>
      <rect x={m.l} y={yv(c.hi)} width={plotW} height={Math.max(0, yv(c.lo) - yv(c.hi))} fill="rgba(16,185,129,.055)" />
      <rect x={m.l} y={yv(hiAction)} width={plotW} height={Math.max(0, yv(loAction) - yv(hiAction))} fill="rgba(16,185,129,.08)" />
      {[c.lo, c.hi].map(v => <line key={v} x1={m.l} x2={W-m.r} y1={yv(v)} y2={yv(v)} stroke="rgba(248,113,113,.38)" strokeDasharray="5 5" />)}
      <line x1={m.l} x2={W-m.r} y1={yv(target)} y2={yv(target)} stroke="rgba(226,232,240,.35)" strokeDasharray="3 4" />
      {[loAction, hiAction].map(v => <line key={v} x1={m.l} x2={W-m.r} y1={yv(v)} y2={yv(v)} stroke="rgba(245,158,11,.28)" strokeDasharray="3 5" />)}

      {charEvents.map((e, i) => {
        const ex = m.l + ((Number(e.partCountAtProposal) - x0) / Math.max(1, x1 - x0)) * plotW;
        const applied = e.state === 'APPLIED';
        return (
          <g key={e.id || i}>
            <line x1={ex} x2={ex} y1={m.t} y2={H-m.b} stroke={applied ? 'rgba(52,211,153,.55)' : 'rgba(148,163,184,.35)'} strokeDasharray="4 4" />
            <text x={ex + 4} y={m.t + 12} fill={applied ? '#6ee7b7' : '#94a3b8'} fontSize="9">{applied ? 'write' : e.state?.toLowerCase()}</text>
          </g>
        );
      })}

      <path d={path} fill="none" stroke="#38bdf8" strokeWidth="2.2" />
      {history.map((p, i) => <circle key={p.measurementId ?? i} cx={x(p)} cy={y(p)} r={i === history.length - 1 ? 4 : 2.1} fill={i === history.length - 1 ? '#e2e8f0' : '#38bdf8'} />)}

      <text x={m.l - 8} y={yv(c.hi)+3} textAnchor="end" fill="#64748b" fontSize="9">{c.hi}</text>
      <text x={m.l - 8} y={yv(c.lo)+3} textAnchor="end" fill="#64748b" fontSize="9">{c.lo}</text>
      <text x={m.l - 8} y={yv(target)+3} textAnchor="end" fill="#94a3b8" fontSize="9">target {target}</text>
      <text x={m.l} y={H-10} fill="#64748b" fontSize="9">part {x0}</text>
      <text x={W-m.r} y={H-10} fill="#64748b" fontSize="9" textAnchor="end">part {x1}</text>
      <text x={W-m.r} y={m.t+12} fill="#94a3b8" fontSize="10" textAnchor="end">µm</text>
    </svg>
  );
}

export function OverlayChart({ characteristics, actionLimit = 0.6 }) {
  const chars = characteristics.filter(Boolean);
  if (!chars.length || chars.every(c => (c.history || []).length < 2)) return <EmptyChart />;

  const W = 920;
  const H = 280;
  const m = { l: 58, r: 24, t: 20, b: 34 };
  const plotW = W - m.l - m.r;
  const plotH = H - m.t - m.b;
  const [x0, x1] = xDomain(chars);
  const y0 = -1.2;
  const y1 = 1.2;
  const x = p => m.l + ((Number(p.partCount) - x0) / Math.max(1, x1 - x0)) * plotW;
  const yv = v => m.t + (1 - (v - y0) / (y1 - y0)) * plotH;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-h-[250px]" role="img" aria-label="Normalized characteristic overlay">
      <rect x={m.l} y={yv(actionLimit)} width={plotW} height={yv(-actionLimit)-yv(actionLimit)} fill="rgba(16,185,129,.06)" />
      {[1,-1].map(v => <line key={v} x1={m.l} x2={W-m.r} y1={yv(v)} y2={yv(v)} stroke="rgba(248,113,113,.42)" strokeDasharray="5 5" />)}
      {[actionLimit,-actionLimit].map(v => <line key={v} x1={m.l} x2={W-m.r} y1={yv(v)} y2={yv(v)} stroke="rgba(245,158,11,.35)" strokeDasharray="3 5" />)}
      <line x1={m.l} x2={W-m.r} y1={yv(0)} y2={yv(0)} stroke="rgba(226,232,240,.25)" />

      {chars.map((c, ci) => {
        const history = c.history || [];
        const path = history.map((p, i) => `${i ? 'L' : 'M'} ${x(p).toFixed(1)} ${yv(normalized(c,p.value)).toFixed(1)}`).join(' ');
        return <path key={c.id} d={path} fill="none" stroke={SERIES[ci % SERIES.length]} strokeWidth="2" />;
      })}

      <text x={m.l - 8} y={yv(1)+3} textAnchor="end" fill="#f87171" fontSize="9">+limit</text>
      <text x={m.l - 8} y={yv(0)+3} textAnchor="end" fill="#94a3b8" fontSize="9">target</text>
      <text x={m.l - 8} y={yv(-1)+3} textAnchor="end" fill="#f87171" fontSize="9">−limit</text>
      <text x={m.l} y={H-10} fill="#64748b" fontSize="9">part {x0}</text>
      <text x={W-m.r} y={H-10} fill="#64748b" fontSize="9" textAnchor="end">part {x1}</text>

      {chars.map((c, i) => (
        <g key={c.id} transform={`translate(${m.l + i*96} 9)`}>
          <circle r="4" fill={SERIES[i % SERIES.length]} />
          <text x="8" y="3" fill="#cbd5e1" fontSize="9">{c.id}</text>
        </g>
      ))}
    </svg>
  );
}

export function SwimlaneChart({ characteristics, actionLimit = 0.6 }) {
  const chars = characteristics.filter(Boolean);
  if (!chars.length) return <EmptyChart />;

  const laneH = 54;
  const W = 920;
  const H = Math.max(180, 34 + chars.length * laneH + 28);
  const m = { l: 138, r: 22, t: 22, b: 28 };
  const plotW = W - m.l - m.r;
  const [x0, x1] = xDomain(chars);
  const x = p => m.l + ((Number(p.partCount) - x0) / Math.max(1, x1 - x0)) * plotW;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: H }} role="img" aria-label="SPC swimlanes">
      {chars.map((c, ci) => {
        const top = m.t + ci * laneH;
        const mid = top + laneH/2;
        const amp = 18;
        const yv = v => mid - clamp(v, -1.25, 1.25) * amp;
        const history = c.history || [];
        const path = history.map((p, i) => `${i ? 'L' : 'M'} ${x(p).toFixed(1)} ${yv(normalized(c,p.value)).toFixed(1)}`).join(' ');
        const col = SERIES[ci % SERIES.length];
        return (
          <g key={c.id}>
            <rect x={m.l} y={top+5} width={plotW} height={laneH-10} rx="5" fill={ci % 2 ? 'rgba(15,23,42,.34)' : 'rgba(30,41,59,.34)'} />
            <rect x={m.l} y={yv(actionLimit)} width={plotW} height={yv(-actionLimit)-yv(actionLimit)} fill="rgba(16,185,129,.045)" />
            <line x1={m.l} x2={W-m.r} y1={yv(0)} y2={yv(0)} stroke="rgba(148,163,184,.17)" />
            <line x1={m.l} x2={W-m.r} y1={yv(1)} y2={yv(1)} stroke="rgba(248,113,113,.22)" strokeDasharray="4 5" />
            <line x1={m.l} x2={W-m.r} y1={yv(-1)} y2={yv(-1)} stroke="rgba(248,113,113,.22)" strokeDasharray="4 5" />
            {path && <path d={path} fill="none" stroke={col} strokeWidth="1.7" />}
            <text x={m.l-10} y={mid-3} textAnchor="end" fill="#e2e8f0" fontSize="10" fontWeight="700">{c.id} · {c.name}</text>
            <text x={m.l-10} y={mid+12} textAnchor="end" fill={c.correctable ? '#fbbf24' : '#64748b'} fontSize="8">
              {c.correctable ? 'OFFSET' : 'PROCESS'}
            </text>
          </g>
        );
      })}
      <text x={m.l} y={H-8} fill="#64748b" fontSize="9">part {x0}</text>
      <text x={W-m.r} y={H-8} fill="#64748b" fontSize="9" textAnchor="end">part {x1}</text>
    </svg>
  );
}

function EmptyChart() {
  return (
    <div className="h-56 rounded-lg border border-slate-800 bg-slate-950/30 flex items-center justify-center text-sm text-slate-500">
      Measure a few parts to build trend history.
    </div>
  );
}
